import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_URL, formatStatus, getStatusColor, formatFormat, getSportIcon, getPlayerName } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  ArrowLeft, Users, Trophy, Play, Plus, Grid3X3, 
  Award, ChevronRight, X, RefreshCw, Swords, Settings2,
  ArrowUpDown, GripVertical, Check, Shuffle, FileDown, QrCode, AlertCircle
} from 'lucide-react';
import QuickInstruction from '../components/QuickInstruction';
import GroupStageView from '../components/RallyDesk/GroupStageView';

const CompetitionDetail = () => {
  const { tournamentId, competitionId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader, user } = useAuth();
  const [competition, setCompetition] = useState(null);
  const [tournament, setTournament] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [matches, setMatches] = useState([]);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddParticipantOpen, setIsAddParticipantOpen] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [generating, setGenerating] = useState(false);
  
  // Draw management state
  const [isDrawOptionsOpen, setIsDrawOptionsOpen] = useState(false);
  const [seedingOption, setSeedingOption] = useState('random');
  const [manualSeedOrder, setManualSeedOrder] = useState([]);
  const [isEditBracketMode, setIsEditBracketMode] = useState(false);
  const [selectedMatchForSwap, setSelectedMatchForSwap] = useState(null);
  const [bracketData, setBracketData] = useState(null);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [generatingKnockout, setGeneratingKnockout] = useState(false);

  // isAdmin: true if user has admin role OR is the tournament owner OR is a moderator
  // For simplicity, allow any authenticated user (backend validates tournament access)
  const isAdmin = !!user;

  useEffect(() => {
    fetchData();
  }, [tournamentId, competitionId]);

  const fetchData = async () => {
    try {
      const [tournamentRes, compRes, participantsRes, matchesRes, playersRes, teamsRes, divisionsRes] = await Promise.all([
        axios.get(`${API_URL}/tournaments/${tournamentId}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/tournaments/${tournamentId}/competitions/${competitionId}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/tournaments/${tournamentId}/competitions/${competitionId}/participants`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/tournaments/${tournamentId}/competitions/${competitionId}/matches`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/tournaments/${tournamentId}/players`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/tournaments/${tournamentId}/teams`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/tournaments/${tournamentId}/divisions`, { headers: getAuthHeader() })
      ]);
      
      setTournament(tournamentRes.data);
      setCompetition(compRes.data);
      setParticipants(participantsRes.data);
      setMatches(matchesRes.data);
      setDivisions(divisionsRes.data || []);
      
      const registeredIds = new Set(participantsRes.data.map(p => p.id));
      const compDivisionId = compRes.data?.division_id;
      const players = playersRes.data || [];
      const teams = teamsRes.data || [];
      // Only show players in this competition's division (when competition has a division)
      const playersNotRegistered = players.filter(p => !registeredIds.has(p.id));
      const availableByDivision = compDivisionId
        ? playersNotRegistered.filter(p => p.division_id === compDivisionId)
        : playersNotRegistered;
      setAvailablePlayers(availableByDivision);
      // For teams: only show teams whose members are all in this competition's division
      const teamsNotRegistered = teams.filter(t => !registeredIds.has(t.id));
      const availableTeamsFiltered = compDivisionId
        ? teamsNotRegistered.filter(t => (t.player_ids || []).every(pid => {
            const pl = players.find(p => p.id === pid);
            return pl && pl.division_id === compDivisionId;
          }))
        : teamsNotRegistered;
      setAvailableTeams(availableTeamsFiltered);
      
      // Set initial manual seed order
      setManualSeedOrder(participantsRes.data.map(p => p.id));
      
      // Fetch bracket data if draw is generated
      if (compRes.data.status !== 'draft') {
        try {
          const bracketRes = await axios.get(
            `${API_URL}/tournaments/${tournamentId}/competitions/${competitionId}/bracket`,
            { headers: getAuthHeader() }
          );
          setBracketData(bracketRes.data);
        } catch (e) {
          // Bracket endpoint might not exist for older data
        }
      }
    } catch (error) {
      toast.error('Failed to load competition data');
      navigate(`/tournaments/${tournamentId}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddParticipants = async () => {
    if (selectedParticipants.length === 0) return;
    try {
      await axios.post(
        `${API_URL}/tournaments/${tournamentId}/competitions/${competitionId}/participants/bulk`,
        selectedParticipants,
        { headers: getAuthHeader() }
      );
      toast.success(`Added ${selectedParticipants.length} participants`);
      setIsAddParticipantOpen(false);
      setSelectedParticipants([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add participants');
    }
  };

  const handleRemoveParticipant = async (participantId) => {
    try {
      await axios.delete(
        `${API_URL}/tournaments/${tournamentId}/competitions/${competitionId}/participants/${participantId}`,
        { headers: getAuthHeader() }
      );
      toast.success('Participant removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove participant');
    }
  };

  const handleGenerateDraw = async () => {
    if (participants.length < 2) {
      toast.error('Need at least 2 participants to generate draw');
      return;
    }
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API_URL}/tournaments/${tournamentId}/competitions/${competitionId}/generate-draw-advanced`,
        {
          seeding: seedingOption,
          seed_order: seedingOption === 'manual' ? manualSeedOrder : null
        },
        { headers: getAuthHeader() }
      );
      toast.success(response.data.message);
      setIsDrawOptionsOpen(false);
      setCompetition((prev) => (prev ? { ...prev, status: 'draw_generated', draw_locked: true } : null));
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate draw');
    } finally {
      setGenerating(false);
    }
  };

  const handleSwapParticipants = async (match1Id, match2Id, swapType) => {
    try {
      await axios.post(
        `${API_URL}/tournaments/${tournamentId}/competitions/${competitionId}/swap-participants`,
        { match1_id: match1Id, match2_id: match2Id, swap_type: swapType },
        { headers: getAuthHeader() }
      );
      toast.success('Participants swapped');
      setSelectedMatchForSwap(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to swap participants');
    }
  };

  const handleAdvanceWinner = async (matchId, winnerId) => {
    try {
      await axios.post(
        `${API_URL}/tournaments/${tournamentId}/competitions/${competitionId}/advance-winner`,
        { match_id: matchId, winner_id: winnerId },
        { headers: getAuthHeader() }
      );
      toast.success('Winner advanced to next round');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to advance winner');
    }
  };

  const moveSeedUp = (idx) => {
    if (idx === 0) return;
    const newOrder = [...manualSeedOrder];
    [newOrder[idx - 1], newOrder[idx]] = [newOrder[idx], newOrder[idx - 1]];
    setManualSeedOrder(newOrder);
  };

  const moveSeedDown = (idx) => {
    if (idx === manualSeedOrder.length - 1) return;
    const newOrder = [...manualSeedOrder];
    [newOrder[idx], newOrder[idx + 1]] = [newOrder[idx + 1], newOrder[idx]];
    setManualSeedOrder(newOrder);
  };

  const getParticipantName = (participantId) => {
    if (!participantId) return 'TBD';
    const player = participants.find(p => p.id === participantId);
    if (player) {
      return `${player.first_name || ''} ${player.last_name || ''}`.trim() || player.name || 'Unknown';
    }
    return bracketData?.participants?.[participantId]?.name || 'Unknown';
  };

  const handleUnlockDraw = async () => {
    try {
      await axios.post(
        `${API_URL}/tournaments/${tournamentId}/competitions/${competitionId}/unlock-draw`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Draw unlocked. You can regenerate the draw (this will replace all matches).');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock draw');
    }
  };

  const handleResetDraw = async () => {
    setResetting(true);
    try {
      await axios.post(
        `${API_URL}/tournaments/${tournamentId}/competitions/${competitionId}/reset-draw`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Draw reset successfully');
      setIsResetDialogOpen(false);
      setCompetition((prev) => (prev ? { ...prev, status: 'draft', draw_locked: false } : null));
      await fetchData();
    } catch (error) {
      console.error('Reset draw error:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset draw');
    } finally {
      setResetting(false);
    }
  };

  const handleDownloadScoreSheet = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/tournaments/${tournamentId}/competitions/${competitionId}/score-sheet-pdf`,
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `score_sheet_${competition.name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Score sheet downloaded');
    } catch (error) {
      toast.error('Failed to download score sheet');
    }
  };

  const handleGenerateRefereeAccess = async (matchId) => {
    try {
      const response = await axios.post(
        `${API_URL}/tournaments/${tournamentId}/matches/${matchId}/referee-access`,
        {},
        { headers: getAuthHeader() }
      );
      
      toast.success('Referee access generated!');
      return response.data;
    } catch (error) {
      toast.error('Failed to generate referee access');
      return null;
    }
  };

  const handleConfirmScore = async (matchId) => {
    try {
      await axios.post(
        `${API_URL}/tournaments/${tournamentId}/matches/${matchId}/confirm-score`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Score confirmed!');
      fetchData();
    } catch (error) {
      toast.error('Failed to confirm score');
    }
  };

  const handleSetFinalists = async () => {
    try {
      await axios.post(
        `${API_URL}/tournaments/${tournamentId}/competitions/${competitionId}/set-finalists`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Finalists set from standings (1st vs 2nd)');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set finalists');
    }
  };

  const handleGenerateKnockout = async () => {
    try {
      setGeneratingKnockout(true);
      await axios.post(
        `${API_URL}/tournaments/${tournamentId}/competitions/${competitionId}/generate-knockout`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Knockout bracket generated (semi-finals + final)');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate knockout');
    } finally {
      setGeneratingKnockout(false);
    }
  };

  const toggleParticipant = (id) => {
    setSelectedParticipants(prev => 
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  // Group matches by round (normalize to number so keys are consistent)
  const matchesByRound = matches.reduce((acc, match) => {
    const round = Number(match.round_number);
    if (!Number.isInteger(round)) return acc;
    if (!acc[round]) acc[round] = [];
    acc[round].push(match);
    return acc;
  }, {});

  // Infer Round 2 from round 1's next_match_id (in case round_number is wrong or missing)
  const round1Matches = matchesByRound[1] || [];
  const round2MatchIds = new Set(round1Matches.flatMap((m) => (m.next_match_id ? [m.next_match_id] : [])));
  const inferredRound2 = round2MatchIds.size > 0
    ? matches.filter((m) => round2MatchIds.has(m.id))
    : [];
  if (inferredRound2.length > 0 && (!matchesByRound[2] || matchesByRound[2].length === 0)) {
    matchesByRound[2] = inferredRound2;
  }

  // Group matches by group (for group stage)
  const matchesByGroup = matches.reduce((acc, match) => {
    const group = match.group_number || 0;
    if (!acc[group]) acc[group] = [];
    acc[group].push(match);
    return acc;
  }, {});

  const availableList = competition?.participant_type === 'single' ? availablePlayers : availableTeams;

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!competition) {
    return <div className="p-6">Competition not found</div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="competition-detail-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <Button variant="ghost" onClick={() => navigate(`/tournaments/${tournamentId}`)} className="w-fit">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Tournament
        </Button>
        
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-xl bg-primary/20 flex items-center justify-center">
              <span className="text-3xl">{getSportIcon(competition.sport)}</span>
            </div>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight uppercase">
                  {competition.name}
                </h1>
                <Badge className={getStatusColor(competition.status)}>
                  {formatStatus(competition.status)}
                </Badge>
              </div>
              <p className="text-muted-foreground">
                {formatFormat(competition.format)} • {competition.participant_type === 'single' ? 'Singles' : competition.participant_type === 'pair' ? 'Doubles' : 'Teams'}
              </p>
            </div>
          </div>
          
          {isAdmin && (
            <div className="flex gap-2">
              {competition.status === 'draft' && participants.length >= 2 && (
                <Dialog open={isDrawOptionsOpen} onOpenChange={setIsDrawOptionsOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="font-bold uppercase tracking-wider"
                      data-testid="generate-draw-btn"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Generate Draw
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border-border max-w-2xl">
                    <DialogHeader>
                      <DialogTitle className="font-heading uppercase">Draw Options</DialogTitle>
                      <DialogDescription>
                        Configure seeding and generate the competition bracket
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-6">
                      {/* Seeding Options */}
                      <div className="space-y-3">
                        <Label className="text-sm font-medium">Seeding Method</Label>
                        <div className="grid grid-cols-3 gap-3">
                          <Button
                            variant={seedingOption === 'random' ? 'default' : 'outline'}
                            className="flex flex-col h-auto py-4"
                            onClick={() => setSeedingOption('random')}
                          >
                            <Shuffle className="w-5 h-5 mb-2" />
                            <span className="text-sm">Random</span>
                          </Button>
                          <Button
                            variant={seedingOption === 'rating' ? 'default' : 'outline'}
                            className="flex flex-col h-auto py-4"
                            onClick={() => setSeedingOption('rating')}
                          >
                            <Trophy className="w-5 h-5 mb-2" />
                            <span className="text-sm">By Rating</span>
                          </Button>
                          <Button
                            variant={seedingOption === 'manual' ? 'default' : 'outline'}
                            className="flex flex-col h-auto py-4"
                            onClick={() => setSeedingOption('manual')}
                          >
                            <GripVertical className="w-5 h-5 mb-2" />
                            <span className="text-sm">Manual</span>
                          </Button>
                        </div>
                      </div>

                      {/* Manual Seed Order */}
                      {seedingOption === 'manual' && (
                        <div className="space-y-3">
                          <Label className="text-sm font-medium">Seed Order (drag to reorder)</Label>
                          <ScrollArea className="h-64 border border-border rounded-lg p-2">
                            {manualSeedOrder.map((participantId, idx) => {
                              const p = participants.find(p => p.id === participantId);
                              const name = p ? `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.name : 'Unknown';
                              return (
                                <div
                                  key={participantId}
                                  className="flex items-center gap-3 p-2 rounded hover:bg-muted/50 group"
                                >
                                  <span className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center font-bold text-sm">
                                    {idx + 1}
                                  </span>
                                  <span className="flex-1">{name}</span>
                                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-7 w-7"
                                      onClick={() => moveSeedUp(idx)}
                                      disabled={idx === 0}
                                    >
                                      <ArrowUpDown className="w-4 h-4 rotate-180" />
                                    </Button>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-7 w-7"
                                      onClick={() => moveSeedDown(idx)}
                                      disabled={idx === manualSeedOrder.length - 1}
                                    >
                                      <ArrowUpDown className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                          </ScrollArea>
                        </div>
                      )}

                      <div className="flex justify-end gap-3">
                        <Button variant="outline" onClick={() => setIsDrawOptionsOpen(false)}>
                          Cancel
                        </Button>
                        <Button 
                          onClick={handleGenerateDraw}
                          disabled={generating}
                          data-testid="confirm-generate-draw"
                        >
                          {generating ? 'Generating...' : 'Generate Draw'}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
              {competition.status !== 'draft' && (
                <>
                  <Button 
                    variant={isEditBracketMode ? 'default' : 'outline'}
                    onClick={() => setIsEditBracketMode(!isEditBracketMode)}
                    data-testid="edit-bracket-btn"
                    title="Swap players between matches for minor tweaks after the draw"
                  >
                    <Settings2 className="w-4 h-4 mr-2" />
                    {isEditBracketMode ? 'Done Editing' : 'Tweak draw (swap players)'}
                  </Button>
                  {competition.draw_locked && (
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={handleUnlockDraw}
                      title="Allow regenerating the draw (then use Generate Draw again)"
                    >
                      Unlock draw
                    </Button>
                  )}
                  <Button 
                    variant="outline"
                    onClick={() => setIsResetDialogOpen(true)}
                    data-testid="reset-draw-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reset Draw
                  </Button>
                </>
              )}
              {matches.length > 0 && (
                <Button 
                  variant="outline"
                  onClick={handleDownloadScoreSheet}
                  data-testid="download-scoresheet-btn"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Score Sheet PDF
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <QuickInstruction text="Add participants → Generate draw → Run matches → Scores update standings automatically." />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border/40">
          <CardContent className="p-4 flex items-center gap-3">
            <Users className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground uppercase">Participants</p>
              <p className="font-medium">{participants.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/40">
          <CardContent className="p-4 flex items-center gap-3">
            <Swords className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground uppercase">Matches</p>
              <p className="font-medium">{matches.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/40">
          <CardContent className="p-4 flex items-center gap-3">
            <Trophy className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground uppercase">Completed</p>
              <p className="font-medium">{matches.filter(m => m.status === 'completed').length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/40">
          <CardContent className="p-4 flex items-center gap-3">
            <Grid3X3 className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground uppercase">Sets to Win</p>
              <p className="font-medium">{competition.scoring_rules?.sets_to_win || 3}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="bracket" className="w-full">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="bracket" data-testid="bracket-tab">Bracket</TabsTrigger>
          <TabsTrigger value="participants" data-testid="participants-tab">Participants</TabsTrigger>
          <TabsTrigger value="matches" data-testid="matches-tab">All Matches</TabsTrigger>
        </TabsList>

        {/* Bracket Tab */}
        <TabsContent value="bracket" className="mt-4">
          {tournament?.settings?.note_of_the_day && (
            <div className="mb-4 p-3 rounded-lg bg-muted/50 border border-border/40 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Note of the day:</span> {tournament.settings.note_of_the_day}
            </div>
          )}
          {matches.length > 0 ? (
            competition.format === 'groups_knockout' ? (
              /* Groups + Knockout View */
              <GroupStageView
                tournamentId={tournamentId}
                competitionId={competitionId}
                competition={competition}
                matches={matches}
                participants={participants}
                getAuthHeader={getAuthHeader}
                isAdmin={isAdmin}
                onRefresh={fetchData}
              />
            ) : (
              /* Standard Bracket View */
              <Card className="bg-card border-border/40">
                <CardHeader>
                  <CardTitle className="font-heading uppercase">Tournament Bracket</CardTitle>
                  <CardDescription>{formatFormat(competition.format)}</CardDescription>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const roundKeys = Object.keys(matchesByRound).map(Number).filter(Number.isInteger).sort((a, b) => a - b);
                    const hasMultipleRounds = roundKeys.length > 1;
                    const isRoundRobin = competition?.format === 'round_robin';
                    const participantCount = participants?.length ?? 0;
                    const needsRegenerate = competition?.format === 'single_elimination' && !hasMultipleRounds && participantCount >= 4;
                    const finalNeedsFinalists = isRoundRobin && matches.some((m) => m.bracket_position === 'Final' && m.participant1_id == null && m.participant2_id == null);
                    // Round-robin phase complete when all matches with round < 100 are done, excluding TBD Final placeholder
                    const roundRobinGroupMatches = matches.filter((m) => (m.round_number ?? 0) < 100);
                    const isTbdFinal = (m) => m.bracket_position === 'Final' && m.participant1_id == null && m.participant2_id == null;
                    const roundRobinPhaseComplete = isRoundRobin && roundRobinGroupMatches.length > 0 && roundRobinGroupMatches.every((m) => m.status === 'completed' || isTbdFinal(m));
                    const hasKnockout = isRoundRobin && matches.some((m) => (m.round_number ?? 0) >= 100);
                    const showRoundRobinDoneBanner = isRoundRobin && roundRobinPhaseComplete && !hasKnockout;
                    return (
                      <>
                        {isRoundRobin && !hasMultipleRounds && !hasKnockout && (
                          <div className="mb-3 p-3 rounded bg-amber-900/80 border border-amber-600 text-sm text-amber-100">
                            <strong>Round Robin</strong> with a single round. Regenerate the draw to get rounds (Round 1, Round 2, …) plus a <strong>Final</strong> match.
                          </div>
                        )}
                        {needsRegenerate && (
                          <div className="mb-3 p-3 rounded bg-blue-900/80 border border-blue-600 text-sm text-blue-100">
                            You have {participantCount} participants. <strong>Regenerate the draw</strong> (Generate draw / Draw with options) to create Round 2 and the rest of the bracket.
                          </div>
                        )}
                        {showRoundRobinDoneBanner && (
                          <div className="mb-3 p-3 rounded bg-blue-900/80 border border-blue-600 text-sm text-blue-100 flex flex-wrap items-center gap-3">
                            <span>Round robin done. Choose:</span>
                            {finalNeedsFinalists && (
                              <Button size="sm" variant="secondary" onClick={handleSetFinalists}>Set finalists from standings (1st vs 2nd)</Button>
                            )}
                            <Button size="sm" variant="default" onClick={handleGenerateKnockout} disabled={generatingKnockout}>
                              {generatingKnockout ? 'Generating…' : 'Generate Knockout (top 50% → semi-finals + final)'}
                            </Button>
                          </div>
                        )}
                        {hasMultipleRounds && (
                          <div className="mb-3 p-2 rounded bg-muted/50 text-center text-sm text-muted-foreground">
                            Rounds: {roundKeys.join(' → ')}. Scroll right to see Round 2 and later.
                          </div>
                        )}
                        <div className={`w-full overflow-y-visible pb-4 ${hasMultipleRounds ? 'overflow-x-scroll' : 'overflow-x-auto'}`} style={hasMultipleRounds ? { scrollbarGutter: 'stable' } : undefined}>
                          <div className="flex gap-8 min-w-max">
                      {Object.keys(matchesByRound).map(Number).filter(Number.isInteger).sort((a, b) => a - b).map((round) => (
                        <div key={round} className="min-w-[300px] flex-shrink-0">
                          <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-4">
                            {round > 100
                              ? `Knockout R${round - 100}`
                              : competition?.format === 'round_robin' && matchesByRound[round]?.some((m) => m.bracket_position === 'Final')
                                ? 'Final'
                                : `Round ${round}`}
                          </h3>
                          <div className="space-y-4">
                            {matchesByRound[round].map((match) => (
                              <div
                                key={match.id}
                                className={`border rounded-lg overflow-hidden transition-colors ${
                                  match.status === 'live' ? 'border-red-500 animate-pulse' :
                                  match.score_status === 'pending' && match.scores?.length > 0 ? 'border-amber-500 bg-amber-500/5 ring-1 ring-amber-500/50' :
                                  selectedMatchForSwap?.id === match.id ? 'border-primary ring-2 ring-primary' :
                                  isEditBracketMode && selectedMatchForSwap ? 'border-primary/50 cursor-pointer hover:border-primary' :
                                  'border-border/40 cursor-pointer hover:border-primary/50'
                                }`}
                                onClick={() => {
                                  if (isEditBracketMode && selectedMatchForSwap && selectedMatchForSwap.id !== match.id) {
                                    // Swap with selected match
                                    handleSwapParticipants(selectedMatchForSwap.id, match.id, 'both');
                                  } else if (isEditBracketMode) {
                                    // Select this match for swapping
                                    setSelectedMatchForSwap(match);
                                  } else if (match.participant1_id && match.participant2_id) {
                                    navigate(`/tournaments/${tournamentId}/matches/${match.id}`);
                                  }
                                }}
                                data-testid={`bracket-match-${match.id}`}
                              >
                                {/* Pending approval indicator */}
                                {!isEditBracketMode && match.score_status === 'pending' && match.scores?.length > 0 && (
                                  <div className="px-3 py-1.5 bg-amber-500/20 border-b border-amber-500/40 flex items-center justify-center">
                                    <span className="text-xs font-medium text-amber-600 dark:text-amber-400">Score pending approval</span>
                                  </div>
                                )}
                                {/* Edit Mode Header */}
                                {isEditBracketMode && (
                                  <div className="px-3 py-1.5 bg-primary/10 border-b border-border/40 flex items-center justify-between">
                                    <span className="text-xs font-medium text-primary">
                                      {selectedMatchForSwap?.id === match.id ? 'Selected - Click another match to swap' : 'Click to select'}
                                    </span>
                                    {selectedMatchForSwap?.id === match.id && (
                                      <Button size="sm" variant="ghost" className="h-6 px-2" onClick={(e) => { e.stopPropagation(); setSelectedMatchForSwap(null); }}>
                                        <X className="w-3 h-3" />
                                      </Button>
                                    )}
                                  </div>
                                )}
                                
                                {/* Participant 1 */}
                                <div className={`p-3 flex items-center justify-between ${
                                  match.winner_id === match.participant1_id ? 'bg-green-500/10' : 'bg-muted/30'
                                }`}>
                                  <div className="flex items-center gap-2">
                                    {match.seed1 && <span className="text-xs text-muted-foreground">[{match.seed1}]</span>}
                                    {match.winner_id === match.participant1_id && (
                                      <Award className="w-4 h-4 text-green-500" />
                                    )}
                                    <span className={match.winner_id === match.participant1_id ? 'font-semibold' : ''}>
                                      {match.participant1?.name || getParticipantName(match.participant1_id)}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {match.scores?.length > 0 && (
                                      <span className="font-teko text-xl">
                                        {match.scores.filter(s => s.score1 > s.score2).length}
                                      </span>
                                    )}
                                    {/* Manual advance button */}
                                    {isEditBracketMode && match.participant1_id && match.participant2_id && match.status !== 'completed' && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 px-2"
                                        onClick={(e) => { e.stopPropagation(); handleAdvanceWinner(match.id, match.participant1_id); }}
                                      >
                                        <Check className="w-3 h-3" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="border-t border-border/40" />
                                
                                {/* Participant 2 */}
                                <div className={`p-3 flex items-center justify-between ${
                                  match.winner_id === match.participant2_id ? 'bg-green-500/10' : 'bg-muted/30'
                                }`}>
                                  <div className="flex items-center gap-2">
                                    {match.seed2 && <span className="text-xs text-muted-foreground">[{match.seed2}]</span>}
                                    {match.winner_id === match.participant2_id && (
                                      <Award className="w-4 h-4 text-green-500" />
                                    )}
                                    <span className={match.winner_id === match.participant2_id ? 'font-semibold' : ''}>
                                      {match.participant2?.name || getParticipantName(match.participant2_id)}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {match.scores?.length > 0 && (
                                      <span className="font-teko text-xl">
                                        {match.scores.filter(s => s.score2 > s.score1).length}
                                      </span>
                                    )}
                                    {/* Manual advance button */}
                                    {isEditBracketMode && match.participant1_id && match.participant2_id && match.status !== 'completed' && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 px-2"
                                        onClick={(e) => { e.stopPropagation(); handleAdvanceWinner(match.id, match.participant2_id); }}
                                      >
                                        <Check className="w-3 h-3" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Match action */}
                                {!isEditBracketMode && match.participant1_id && match.participant2_id && match.status !== 'completed' && (
                                  <div className="p-2 bg-muted/20 border-t border-border/40">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="w-full text-xs"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/tournaments/${tournamentId}/matches/${match.id}`);
                                      }}
                                    >
                                      {match.status === 'live' ? 'Continue Match' : 'Start Match'}
                                      <ChevronRight className="w-4 h-4 ml-1" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                  
                  {/* Edit Mode Instructions */}
                  {isEditBracketMode && (
                    <div className="mt-4 p-4 bg-primary/10 border border-primary/20 rounded-lg">
                      <h4 className="font-medium mb-2">Tweak draw (minor changes)</h4>
                      <p className="text-sm text-muted-foreground mb-2">Use this to swap players between matches for unknown or last-minute reasons—e.g. avoid same club, fix a mistake, or accommodate a request.</p>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• Click a match to select it, then click another match to swap all participants</li>
                        <li>• Click the checkmark (✓) next to a player to manually advance them as winner</li>
                        <li>• Click &quot;Done Editing&quot; when finished</li>
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          ) : (
            <Card className="bg-card border-border/40">
              <CardContent className="py-12 text-center text-muted-foreground">
                <Grid3X3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="mb-2">No bracket generated yet</p>
                <p className="text-sm">Add at least 2 participants and generate the draw</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Participants Tab */}
        <TabsContent value="participants" className="mt-4">
          <Card className="bg-card border-border/40">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading uppercase">Participants</CardTitle>
                <CardDescription>{participants.length} registered</CardDescription>
              </div>
              {isAdmin && competition.status === 'draft' && (
                <Dialog open={isAddParticipantOpen} onOpenChange={setIsAddParticipantOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="add-participant-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Participants
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border-border max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="font-heading uppercase">Add Participants</DialogTitle>
                      <DialogDescription>
                        {competition.division_id ? (
                          <>Showing {competition.participant_type === 'single' ? 'players' : 'teams'} from <strong>{divisions.find(d => d.id === competition.division_id)?.name || 'this division'}</strong> only</>
                        ) : (
                          <>Select {competition.participant_type === 'single' ? 'players' : 'teams'} to add</>
                        )}
                      </DialogDescription>
                    </DialogHeader>
                    <ScrollArea className="max-h-[400px]">
                      <div className="space-y-2">
                        {availableList.length > 0 && (
                          <div
                            className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-border/40 cursor-pointer hover:bg-muted/70"
                            onClick={() => {
                              if (selectedParticipants.length === availableList.length) {
                                setSelectedParticipants([]);
                              } else {
                                setSelectedParticipants(availableList.map((i) => i.id));
                              }
                            }}
                          >
                            <Checkbox
                              checked={selectedParticipants.length === availableList.length}
                              onCheckedChange={() => {
                                if (selectedParticipants.length === availableList.length) {
                                  setSelectedParticipants([]);
                                } else {
                                  setSelectedParticipants(availableList.map((i) => i.id));
                                }
                              }}
                            />
                            <span className="font-medium text-sm">
                              {selectedParticipants.length === availableList.length ? 'Deselect all' : 'Select all'} ({availableList.length})
                            </span>
                          </div>
                        )}
                        {availableList.length > 0 ? availableList.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 cursor-pointer"
                            onClick={() => toggleParticipant(item.id)}
                          >
                            <Checkbox
                              checked={selectedParticipants.includes(item.id)}
                              onCheckedChange={() => toggleParticipant(item.id)}
                            />
                            <div className="min-w-0">
                              <span className="font-medium">{item.name || `${item.first_name || ''} ${item.last_name || ''}`.trim()}</span>
                              {competition.participant_type === 'single' && (item.division_name != null || item.age != null || item.rating != null) && (
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {[item.division_name, item.age != null ? `Age ${item.age}` : null, item.rating != null ? `Rating ${item.rating}` : null, item.club].filter(Boolean).join(' · ')}
                                </p>
                              )}
                              {competition.participant_type !== 'single' && item.player_names?.length > 0 && (
                                <p className="text-xs text-muted-foreground mt-0.5">{item.player_names.join(', ')}</p>
                              )}
                            </div>
                          </div>
                        )) : (
                          <p className="text-center text-muted-foreground py-4">
                            {competition.division_id
                              ? `No available ${competition.participant_type === 'single' ? 'players' : 'teams'} in this division. Add players to division "${divisions.find(d => d.id === competition.division_id)?.name || 'this competition'}" first.`
                              : `No available ${competition.participant_type === 'single' ? 'players' : 'teams'}`}
                          </p>
                        )}
                      </div>
                    </ScrollArea>
                    <Button 
                      onClick={handleAddParticipants} 
                      className="w-full"
                      disabled={selectedParticipants.length === 0}
                      data-testid="confirm-add-participants"
                    >
                      Add {selectedParticipants.length} Participants
                    </Button>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent>
              {participants.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {participants.map((participant, idx) => (
                    <div
                      key={participant.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/30"
                      data-testid={`participant-${participant.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center font-teko text-lg flex-shrink-0">
                          {idx + 1}
                        </span>
                        <div className="min-w-0">
                          <span className="font-medium block">
                            {participant.name || `${participant.first_name || ''} ${participant.last_name || ''}`.trim()}
                          </span>
                          {(participant.division_name != null || participant.age != null || participant.rating != null || participant.club) && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {[participant.division_name, participant.age != null ? `Age ${participant.age}` : null, participant.rating != null ? `Rating ${participant.rating}` : null, participant.club].filter(Boolean).join(' · ')}
                            </p>
                          )}
                          {participant.player_names?.length > 0 && (
                            <p className="text-xs text-muted-foreground mt-0.5">{participant.player_names.join(', ')}</p>
                          )}
                        </div>
                      </div>
                      {isAdmin && competition.status === 'draft' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveParticipant(participant.id)}
                          className="text-muted-foreground hover:text-destructive flex-shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No participants yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Matches Tab */}
        <TabsContent value="matches" className="mt-4">
          {tournament?.settings?.note_of_the_day && (
            <div className="mb-4 p-3 rounded-lg bg-muted/50 border border-border/40 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Note of the day:</span> {tournament.settings.note_of_the_day}
            </div>
          )}
          <Card className="bg-card border-border/40">
            <CardHeader>
              <CardTitle className="font-heading uppercase">All Matches</CardTitle>
              <CardDescription>
                {matches.filter(m => m.status === 'completed').length} / {matches.length} completed
              </CardDescription>
            </CardHeader>
            <CardContent>
              {matches.length > 0 ? (
                <div className="space-y-3">
                  {matches.map((match) => (
                    <div
                      key={match.id}
                      className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer hover:bg-muted/30 transition-colors ${
                        match.status === 'live' ? 'border-red-500 bg-red-500/5' : 
                        match.status === 'completed' ? 'border-border/40 bg-muted/20' : 'border-border/40'
                      }`}
                      onClick={() => match.participant1_id && match.participant2_id && navigate(`/tournaments/${tournamentId}/matches/${match.id}`)}
                      data-testid={`match-row-${match.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="text-center min-w-[60px]">
                          <p className="text-xs text-muted-foreground uppercase">
                            {match.group_number ? `G${match.group_number}` : `R${match.round_number}`}
                          </p>
                          <p className="font-teko text-xl">M{match.match_number}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={match.winner_id === match.participant1_id ? 'font-semibold text-green-400' : ''}>
                            {match.participant1?.name || getPlayerName(match.participant1) || 'TBD'}
                          </span>
                          <span className="text-muted-foreground">vs</span>
                          <span className={match.winner_id === match.participant2_id ? 'font-semibold text-green-400' : ''}>
                            {match.participant2?.name || getPlayerName(match.participant2) || 'TBD'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {match.scores?.length > 0 && (
                          <div className="flex items-center gap-2 font-teko text-xl">
                            <span>{match.scores.filter(s => s.score1 > s.score2).length}</span>
                            <span className="text-muted-foreground">-</span>
                            <span>{match.scores.filter(s => s.score2 > s.score1).length}</span>
                          </div>
                        )}
                        <Badge className={getStatusColor(match.status)}>
                          {formatStatus(match.status)}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Swords className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No matches yet. Generate draw first.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Reset Draw Confirmation Dialog */}
      <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <AlertCircle className="w-5 h-5" />
              Reset Draw
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to reset the draw? This will <strong>delete all matches</strong> including completed ones and their scores. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button 
              variant="outline" 
              onClick={() => setIsResetDialogOpen(false)}
              disabled={resetting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleResetDraw}
              disabled={resetting}
              data-testid="confirm-reset-btn"
            >
              {resetting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Yes, Reset Draw
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompetitionDetail;
